import React, { useEffect, useState, useRef, ReactElement } from 'react';

import HeaderContext from './context/HeaderContext';
import HeaderNavBarContext from './context/HeaderNavBarContext';
import HeaderSearchBarContext from './context/HeaderSearchBarContext';

import Box from '../../components/Box/Box';
import MyAccountMobileMenu from './MyAccountMobileMenu';
import NavBar from './Navbar';
import SearchBar from './SearchBar';

import { isSmbFlaggedFromSessionStorageCache } from './utils/fromSessionStorageCache';
import fetchApI from './utils/fetchApi';
import { addClass, removeClass } from './utils/className';
import type { THeaderConfig, TNavBarData } from '../../types/THeaderConfig';

type THeader = {
  headerConfig: THeaderConfig;
  children?: ReactElement;
  trackGAEvent?: (eventName: string, eventAction: string) => void;
  className?: string;
};

export function isValidNavBarConfig(navBarConfig: TNavBarData): boolean {
  return navBarConfig && Object.entries(navBarConfig).length !== 0;
}

export function getLoggedInState(navBarConfig: TNavBarData): boolean {
  return navBarConfig?.userDetails?.isLoggedIn || false;
}

function getNavBarStatus(navBarConfig: TNavBarData) {
  let isLoggedIn = false;
  let smbStatusUrl = '';

  if (isValidNavBarConfig(navBarConfig)) {
    isLoggedIn = getLoggedInState(navBarConfig);
    smbStatusUrl = navBarConfig?.smbStatusUrl ?? '';
  }
  return { smbStatusUrl, isLoggedIn };
}

const Header = ({ headerConfig, children, trackGAEvent }: THeader) => {
  const {
    showNavBar = true,
    showSearchBar = false,
    navBar,
    searchBar,
    isSticky = false,
    logoUrls: { logoMobileUrl, logoDesktopUrl },
    ...commonConfig
  } = headerConfig;

  const { searchBarTranslationsForTenant, ...searchBarConfig } = searchBar ?? {};

  const { menuLinks: initialMenuLinks, translations, menuTitle, ...navBarConfig } = navBar ?? ({} as TNavBarData);

  const { smbStatusUrl, isLoggedIn } = getNavBarStatus(navBarConfig);

  const [menuLinks, setMenuLinks] = useState(initialMenuLinks);
  const [isHamburgerMenuVisible, setHamburgerMenuVisible] = useState(false);
  const [isExpandSearchBar, setExpandSearchBar] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const toggleHamburgerMenuClass = isHamburgerMenuVisible ? addClass : removeClass;
    toggleHamburgerMenuClass(document.body, 'hz-no-body-scroll');
  }, [isHamburgerMenuVisible]);

  useEffect(() => {
    const EVENT_OPEN_MENU = 'Header:OpenMenu';
    const handleOpenMenu = () => setHamburgerMenuVisible(true);
    window.addEventListener(EVENT_OPEN_MENU, handleOpenMenu);
    return () => window.removeEventListener(EVENT_OPEN_MENU, handleOpenMenu);
  }, []);

  useEffect(() => {
    if (smbStatusUrl && isLoggedIn) {
      const fetchData = async () => {
        const isFlagged = await isSmbFlaggedFromSessionStorageCache(fetchApI, smbStatusUrl);
        if (isFlagged) {
          setMenuLinks((prevState) =>
            prevState?.map(({ show, role, ...rest }) => {
              const newShow = role === 'smbStatus' ? isFlagged : show;
              return { show: newShow, role, ...rest };
            }),
          );
        }
      };

      fetchData();
    }
  }, [smbStatusUrl, isLoggedIn]);

  const showHamburgerMenu = () => setHamburgerMenuVisible(true);
  const hideHamburgerMenu = () => setHamburgerMenuVisible(false);

  const showHamburgerMenuClassName = isHamburgerMenuVisible ? 'hz-Header--showHamburgerMenu' : '';
  const expandSearchBarClassName = isExpandSearchBar ? 'hz-Header--expandSearchBar' : '';
  const stickyHeaderClassName = isSticky ? ' u-stickyHeader' : '';

  return (
    <HeaderContext.Provider value={commonConfig}>
      <HeaderNavBarContext.Provider value={navBarConfig}>
        <header
          ref={ref}
          className={`${showHamburgerMenuClassName}${stickyHeaderClassName}`}
          style={{ height: '100%' }}
        >
          <Box as="div" name="Header" className={expandSearchBarClassName}>
            <Box as="div" parent="Header" name="ribbonTop" />
            {showNavBar && menuLinks && (
              <NavBar
                logoMobileSrc={logoMobileUrl}
                logoDesktopSrc={logoDesktopUrl}
                showHamburgerMenu={showHamburgerMenu}
                menuLinks={menuLinks}
                trackGAEvent={trackGAEvent}
                translations={translations}
                wrapHeadLogoH1Tag={navBarConfig?.wrapHeadLogoH1Tag || false}
                showSkipToMainLink={navBarConfig?.showSkipToMainLink || false}
                showSkipToFooterLink={navBarConfig?.showSkipToFooterLink || false}
              />
            )}
            {showSearchBar && searchBarConfig && (
              <HeaderSearchBarContext.Provider value={searchBarConfig}>
                <SearchBar
                  setExpandSearchBar={setExpandSearchBar}
                  trackGAEvent={trackGAEvent}
                  searchBarTranslationsForTenant={searchBarTranslationsForTenant}
                />
              </HeaderSearchBarContext.Provider>
            )}
            {children}
            <Box as="div" parent="Header" name="ribbonBottom" />
          </Box>
          {showNavBar && (
            <MyAccountMobileMenu isVisible={isHamburgerMenuVisible} hideMenu={hideHamburgerMenu} title={menuTitle} />
          )}
        </header>
      </HeaderNavBarContext.Provider>
    </HeaderContext.Provider>
  );
};

export default Header;
